import React from "react";
import "./Recons.css";
// Import necessary UI components from react-bootstrap and lucide-react
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { Info } from "lucide-react"; 
import { FaSort, FaSortUp, FaSortDown } from "react-icons/fa";
import Pagination from "../../components/Pagination/Pagination";
import {
  formatCurrency,
  formatDateTime,
  // formatDate,
} from "../../components/Constants/Constants";
import { useNavigate } from "react-router-dom";
import AppLoader from "../../components/AppLoader/appLoader";
import { useRecons } from "../../customHooks/Recons/useRecons";
// import usePermission from "../../utils/usePermission";
// import UnAuthorized from "../UnAuthorized/UnAuthorized";
import CopyIcon from "../../components/CopyIcon/CopyIcon";

// Define column information for Tooltips
const reconColumnInfo = {
  reconTxnId: "Unique Transaction ID for the reconciliation request.",
  reconMessageId: "The unique message ID used in the reconciliation request.",
  orderId: "The Order ID (Network Order ID) associated with the reconciliation.",
  orderAmount: "The total order amount used for reconciliation.",
  differenceAmount: "The difference amount calculated during reconciliation.",
  reconType: "The type of reconciliation (e.g., PAYMENT, SETTLEMENT).",
  status: "The current status of the reconciliation request.",
  updatedAt: "The date and time the reconciliation record was last updated.",
};


const Recons = () => {
  const today = new Date().toISOString().slice(0, 10);
  const {
    recons,
    loading,
    totalOrderCount,
    filters,
    setFilters,
    handleFilterChange,
    clearFilters,
    sortConfig,
    handleSort,
    paginatedRecons,
    totalPages,
    currentPage,
    startItem,
    endItem,
    goToPage,
    itemsPerPage,
    setItemsPerPage,
    itemsPerPageOptions,
    handleItemsPerPageChange,
    filterOptions, // Include dynamic filters
    selectedOption,
    setSelectedOption,
    searchValue,
    setSearchValue,
    handleDropdownChange,
    handleSearchChange,
    searchOptions,
    handleReconRaise,
  } = useRecons();

  // const canRaiseRecon = usePermission("RAISE_RECON_ACCESS");

  const navigate = useNavigate();
  return (
    <>
      {/* {canRaiseRecon ? ( */}
      <div className="m-4">
        <p className="text-muted small mb-3">
          This page provides a consolidated view of all reconciliation requests.
          Use the filters below to search for specific requests and click on a
          Recon Txn. ID for detailed information.
        </p>
        <div className="d-flex justify-content-end align-items-center mt-2">
          {/* <button
          className="btn btn-primary d-flex align-items-center"
          style={{ padding: "4px 8px", fontSize: "14px" }}
          onClick={handleReconRaise}
        >
          Raise Recon
        </button> */}
        </div>

        {/* --- Filters (Styled like Orders) --- */}
        <div className="filters-wrapper bg-white px-3 py-2 rounded shadow-sm">
          <div className="row gx-4 gy-3 align-items-end flex-wrap">
            {/* Search Section - Styled like Generic Search in Orders */}
            <div className="col-auto">
              <label className="form-label mb-1">
                Search By
              </label>
              <div className="input-group" style={{ minWidth: "400px" }}>
                <select
                  className="form-select"
                  value={selectedOption}
                  onChange={handleDropdownChange}
                  style={{ maxWidth: "150px" }}
                >
                  {searchOptions?.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <input
                  type="text"
                  className="form-control"
                  value={searchValue}
                  onChange={handleSearchChange}
                  placeholder={`Enter ${selectedOption
                    .split("_")
                    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                    .join(" ")}`}
                  style={{
                    fontSize: "12px",
                    padding: "none",
                  }}
                />
              </div>
            </div>

            {/* Recon Type */}
            <div className="col-auto">
              <label className="form-label mb-1">
                Recon Type
              </label>
              <select
                className="form-select"
                value={filters.recon_type || ""}
                onChange={(e) =>
                  handleFilterChange("recon_type", e.target.value)
                }
                style={{ minWidth: "140px" }}
              >
                <option value="">All</option>
                {filterOptions?.reconType?.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>

            {/* Recon Status */}
            {/* <div className="col-auto">
              <label className="form-label mb-1">
                Recon Status
              </label>
              <select
                className="form-select"
                value={filters.recon_status || ""}
                onChange={(e) =>
                  handleFilterChange("recon_status", e.target.value)
                }
                style={{ minWidth: "120px" }}
              >
                <option value="">All</option>
                {filterOptions?.reconStatuses?.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div> */}

            {/* Created From (Date Range start) - Styled like Orders date pickers */}
            <div className="col-auto">
              <label className="form-label mb-1">Created From</label>
              <input
                type="date"
                className="form-control"
                value={filters.recon_created_at_from}
                onChange={(e) =>
                  handleFilterChange(
                    "recon_created_at_from",
                    e.target.value
                  )
                }
                max={today}
                style={{
                  fontSize: "12px",
                  padding: "none",
                }}
              />
            </div>

            {/* Created To (Date Range end) - Styled like Orders date pickers */}
            <div className="col-auto">
              <label className="form-label mb-1">Created To</label>
              <input
                type="date"
                className="form-control"
                value={filters.recon_created_at_to}
                onChange={(e) =>
                  handleFilterChange("recon_created_at_to", e.target.value)
                }
                max={today}
                min={filters.recon_created_at_from || ""}
                style={{
                  fontSize: "12px",
                  padding: "none",
                }}
              />
            </div>

            {/* Clear Filters Button - Styled like Orders Clear Filters button */}
            <div className="col-auto d-flex">
              <button
                type="button"
                className="btn btn-primary no-hover-btn"
                style={{ padding: "5px 8px" }}
                onClick={clearFilters}
              >
                Reset Filters
              </button>
            </div>
          </div>
        </div>

        {loading ? (
          <AppLoader />
        ) : paginatedRecons && paginatedRecons.length > 0 ? (
          <>
            <div
              className="table-responsive mt-4"
              style={{ overflowX: "auto" }}
            >
              <table
                className="table table-bordered table-hover align-middle text-center"
              >
                <thead className="table-light text-center">
                  <tr>
                    {/* Recon Txn. ID Header with Info icon */}
                    <th>
                      Recon Txn. ID
                      <OverlayTrigger
                        placement="top"
                        overlay={<Tooltip>{reconColumnInfo.reconTxnId}</Tooltip>}
                      >
                        <span style={{ marginLeft: "4px", cursor: "help" }}>
                          <Info size={14} className="text-muted" />
                        </span>
                      </OverlayTrigger>
                    </th>
                    
                    {/* Recon Message ID Header with Info icon */}
                    <th>
                      Recon Message ID
                      <OverlayTrigger
                        placement="top"
                        overlay={<Tooltip>{reconColumnInfo.reconMessageId}</Tooltip>}
                      >
                        <span style={{ marginLeft: "4px", cursor: "help" }}>
                          <Info size={14} className="text-muted" />
                        </span>
                      </OverlayTrigger>
                    </th>
                    
                    {/* Order ID Header with Info icon */}
                    <th>
                      Order ID
                      <OverlayTrigger
                        placement="top"
                        overlay={<Tooltip>{reconColumnInfo.orderId}</Tooltip>}
                      >
                        <span style={{ marginLeft: "4px", cursor: "help" }}>
                          <Info size={14} className="text-muted" />
                        </span>
                      </OverlayTrigger>
                    </th>
                    
                    {/* Order Amt Header with Sort and Info icon */}
                    <th
                      onClick={() => handleSort("order_amount")}
                      role="button"
                      className="cursor-pointer"
                    >
                      Order Amt &nbsp;
                      {sortConfig.key === "order_amount" ? (
                        sortConfig.direction === "asc" ? (
                          <FaSortUp size={14} />
                        ) : (
                          <FaSortDown size={14} />
                        )
                      ) : (
                        <FaSort size={14} />
                      )}
                      <OverlayTrigger
                        placement="top"
                        overlay={<Tooltip>{reconColumnInfo.orderAmount}</Tooltip>}
                      >
                        <span style={{ marginLeft: "4px", cursor: "help" }}>
                          <Info size={14} className="text-muted" />
                        </span>
                      </OverlayTrigger>
                    </th>
                    
                    {/* Difference Amt Header with Sort and Info icon */}
                    <th
                      onClick={() => handleSort("difference_amount")}
                      role="button"
                      className="cursor-pointer"
                    >
                      Difference Amt &nbsp;
                      {sortConfig.key === "difference_amount" ? (
                        sortConfig.direction === "asc" ? (
                          <FaSortUp size={14} />
                        ) : (
                          <FaSortDown size={14} />
                        )
                      ) : (
                        <FaSort size={14} />
                      )}
                      <OverlayTrigger
                        placement="top"
                        overlay={<Tooltip>{reconColumnInfo.differenceAmount}</Tooltip>}
                      >
                        <span style={{ marginLeft: "4px", cursor: "help" }}>
                          <Info size={14} className="text-muted" />
                        </span>
                      </OverlayTrigger>
                    </th>
                    
                    {/* Recon Type Header with Info icon */}
                    <th>
                      Recon Type
                      <OverlayTrigger
                        placement="top"
                        overlay={<Tooltip>{reconColumnInfo.reconType}</Tooltip>}
                      >
                        <span style={{ marginLeft: "4px", cursor: "help" }}>
                          <Info size={14} className="text-muted" />
                        </span>
                      </OverlayTrigger>
                    </th>
                    
                    {/* Status Header with Info icon */}
                    <th>
                      Status
                      <OverlayTrigger
                        placement="top"
                        overlay={<Tooltip>{reconColumnInfo.status}</Tooltip>}
                      >
                        <span style={{ marginLeft: "4px", cursor: "help" }}>
                          <Info size={14} className="text-muted" />
                        </span>
                      </OverlayTrigger>
                    </th>

                    {/* Updated At Header with Sort and Info icon */}
                    <th
                      onClick={() => handleSort("updatedAt")}
                      role="button"
                      className="cursor-pointer"
                    >
                      Updated At &nbsp;
                      {sortConfig.key === "updatedAt" ? (
                        sortConfig.direction === "asc" ? (
                          <FaSortUp size={14} />
                        ) : (
                          <FaSortDown size={14} />
                        )
                      ) : (
                        <FaSort size={14} />
                      )}
                      <OverlayTrigger
                        placement="top"
                        overlay={<Tooltip>{reconColumnInfo.updatedAt}</Tooltip>}
                      >
                        <span style={{ marginLeft: "4px", cursor: "help" }}>
                          <Info size={14} className="text-muted" />
                        </span>
                      </OverlayTrigger>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedRecons?.map((recon, index) => (
                      <tr key={`${recon.recon_transaction_id}-${index}`}>
                        {/* Recon Txn. ID - Removed minWidth */}
                        <td className="copy-cell">
                          <span
                            onClick={() =>
                              navigate(
                                `/recons-detail/${recon.recon_transaction_id}`
                              )
                            }
                            style={{
                              cursor: "pointer",
                              color: "#0d6efd",
                              fontWeight: 500,
                              textDecoration: "none",
                            }}
                            title="View recons Details"
                            className="truncate-cell"
                          >
                            {recon.recon_transaction_id}
                          </span>
                          <CopyIcon textToCopy={recon.recon_transaction_id} />{" "}
                        </td>

                        {/* Recon Message ID - Removed minWidth */}
                        <td className="copy-cell">
                          <span className="truncate-cell">
                            {recon.message_id}
                          </span>
                          <CopyIcon textToCopy={recon.message_id} />
                        </td>

                        {/* Order ID - Removed minWidth */}
                        <td className="copy-cell">
                          <span className="truncate-cell">
                            {recon.order_id}
                          </span>
                          <CopyIcon textToCopy={recon.order_id} />
                        </td>

                        {/* Order Amount - Removed minWidth */}
                        <td>
                          {formatCurrency(recon.order_amount)}
                        </td>

                        {/* Difference Amount - Removed minWidth */}
                        <td>
                          {formatCurrency(recon.difference_amount)}
                        </td>

                        {/* Recon Type - Removed minWidth */}
                        <td>
                          {recon.recon_type}
                        </td>

                        {/* Status - Removed minWidth */}
                        <td>
                          {recon.recon_status}
                        </td>

                        {/* Updated At - Removed minWidth */}
                        <td>
                          {formatDateTime(recon.last_updated)}
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </>
        ) : (
          <div
            style={{
              textAlign: "center",
              padding: "40px",
              color: "#9ca3af",
              fontSize: "14px",
              marginTop: "16px",
            }}
          >
            No Recons Found
          </div>
        )}

        {totalOrderCount > 0 && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            startItem={startItem}
            endItem={endItem}
            totalItems={totalOrderCount}
            itemsPerPage={itemsPerPage}
            onItemsPerPageChange={handleItemsPerPageChange}
            onPageChange={goToPage}
            itemsPerPageOptions={itemsPerPageOptions}
          />
        )}
      </div>
      {/* ) : (
        <UnAuthorized />
      )} */}
    </>
  );
};

export default Recons;